/**
 * DEMOLITION KEYNOTES (2026-10-04, Matt): a demolition plan's demolished items get
 * auto-populated keynote tags that reference an automatic keynote list on the sheet.
 * Matt's decisions:
 *   - PLACEMENT, hybrid: items at the building's edge (exterior wall runs, doors and
 *     windows in exterior walls, anything within a few feet of one) tag out to straight
 *     RAILS just outside the building, one rail per side, so those tags line up in one
 *     clean row/column per side; interior items tag close by, snapped to a shared GRID
 *     so neighboring tags line up too. Leaders never cross each other.
 *   - NUMBERING: one number per instruction — every "Remove door and frame" reads the
 *     same number; the number is the keynote's position in the firm list, so it is the
 *     same on every sheet and project.
 *   - WORDING: a firm list, editable in Templates → Keynotes (Supabase
 *     `demo_keynotes`, database/283). The defaults below seed it and are the fallback
 *     when the list can't be loaded.
 *   - WHAT GETS A TAG: each demolished item; walls one per run. A door, window or wall
 *     fixture that goes only because its wall goes is covered by the wall's note.
 * Derived, never stored (the annotation doctrine). One builder for the live plan,
 * sheet viewports, the PDF and the sheet's keynote list.
 */
import { getTagOffsets, tagScopeKey } from './tagOffsets.js'
import {
  resolveAllElements, listPhases, phaseStatus, hostedPhaseFields,
  flattenPathPts, polylinePointAt, centerlineOffsetFt, composedOverrides, listGrids, listViews,
  normalizeScope, migrateWallScope, faceLayerOrder, wallLayerKeys, scopeVerbOf,
  roofSheathingWork, roofPlaneVerb, eaveEdgeChanges, eavePartsPresent, withResolvePhase, resolveElement,
} from '@aj/bim-kernel'
import { elementFootprint } from '../editing/elementFootprint.js'
import { roofMarkerPoint, roofPlaneArea } from '../editing/demoLayers.js'
import { streetJunctions, pavingSurface } from '@aj/bim-geometry/categories/paving/generate3d'

// ---------- the firm list ----------

const SLIDING = ['slider', 'mirrorSlider', 'multiSlide', 'cornerSlider']
const FOLDING = ['bifold', 'multiBifold']
const GARAGE = ['sectional', 'carriage', 'schweiss']

/** key, what it applies to (for the editor), default wording — order = default numbering */
export const DEFAULT_DEMO_KEYNOTES = [
  ['wall.exterior', 'Exterior wall', 'REMOVE EXTERIOR WALL, INCLUDING FRAMING, SHEATHING, FINISHES AND ALL DOORS AND WINDOWS WITHIN. SHORE AS REQUIRED.'],
  ['wall.interior', 'Interior wall', 'REMOVE INTERIOR WALL, INCLUDING FRAMING, FINISHES AND ALL DOORS WITHIN. PATCH ADJACENT SURFACES TO MATCH.'],
  ['door', 'Door (swing)', 'REMOVE DOOR, FRAME, TRIM AND HARDWARE. PREPARE OPENING FOR NEW WORK OR INFILL AS SHOWN.'],
  ['door.sliding', 'Sliding door', 'REMOVE SLIDING DOOR ASSEMBLY, FRAME, TRACK AND TRIM. PREPARE OPENING FOR NEW WORK OR INFILL AS SHOWN.'],
  ['door.folding', 'Folding door', 'REMOVE FOLDING DOOR ASSEMBLY, TRACK, FRAME AND TRIM.'],
  ['door.pocket', 'Pocket door', 'REMOVE POCKET DOOR, FRAME AND POCKET HARDWARE.'],
  ['door.garage', 'Garage door', 'REMOVE GARAGE DOOR, TRACKS, SPRINGS, OPENER AND HARDWARE.'],
  ['door.cased', 'Cased opening', 'REMOVE CASED OPENING TRIM AND JAMB FINISHES.'],
  ['door.gate', 'Gate', 'REMOVE GATE, POSTS AND HARDWARE.'],
  ['window', 'Window', 'REMOVE WINDOW, FRAME AND TRIM. PREPARE OPENING FOR NEW WORK OR INFILL AS SHOWN.'],
  ['casework', 'Cabinets', 'REMOVE CABINETS, COUNTERTOPS, BACKSPLASH AND HARDWARE.'],
  ['casework.closet', 'Closet shelving', 'REMOVE CLOSET SHELVING, RODS AND SUPPORTS.'],
  ['plumbing.kitchenSink', 'Kitchen sink', 'REMOVE SINK, FAUCET, DISPOSAL AND ASSOCIATED PLUMBING. CAP SUPPLY AND WASTE LINES AT WALL.'],
  ['plumbing.lavatory', 'Lavatory', 'REMOVE LAVATORY, FAUCET AND ASSOCIATED PLUMBING. CAP SUPPLY AND WASTE LINES AT WALL.'],
  ['plumbing.toilet', 'Toilet', 'REMOVE WATER CLOSET AND ASSOCIATED PLUMBING. CAP SUPPLY AND WASTE LINES.'],
  ['plumbing.tub', 'Bathtub', 'REMOVE BATHTUB, FAUCET, VALVE, SURROUND AND ASSOCIATED PLUMBING. CAP SUPPLY AND WASTE LINES.'],
  ['plumbing.shower', 'Shower', 'REMOVE SHOWER ENCLOSURE, VALVE, HEAD, PAN, FINISHES AND ASSOCIATED PLUMBING. CAP SUPPLY AND WASTE LINES.'],
  ['appliance.range', 'Range / cooktop', 'REMOVE RANGE. CAP GAS LINE OR MAKE ELECTRICAL SAFE AS APPLICABLE.'],
  ['appliance.hood', 'Hood', 'REMOVE HOOD AND DUCT. PATCH WALL OR ROOF PENETRATION.'],
  ['appliance.refrigerator', 'Refrigerator', 'REMOVE REFRIGERATOR. CAP WATER LINE.'],
  ['appliance.dishwasher', 'Dishwasher', 'REMOVE DISHWASHER AND ASSOCIATED PLUMBING AND ELECTRICAL. CAP SUPPLY AND DRAIN.'],
  ['appliance.laundry', 'Washer / dryer', 'REMOVE WASHER AND DRYER. CAP SUPPLY AND DRAIN, REMOVE DRYER VENT.'],
  ['fireplace', 'Fireplace / stove', 'REMOVE FIREPLACE OR STOVE, FLUE, HEARTH AND SURROUND. CAP GAS LINE WHERE OCCURS.'],
  ['mech.waterHeater', 'Water heater', 'REMOVE WATER HEATER, VENT AND ASSOCIATED PIPING.'],
  ['mech.hvac', 'Furnace / air handler / boiler', 'REMOVE HVAC EQUIPMENT AND ASSOCIATED DUCTWORK, PIPING AND CONTROLS.'],
  ['mech.condenser', 'Condenser / mini-split', 'REMOVE HVAC UNIT, LINE SET AND DISCONNECT.'],
  ['mech.register', 'Register / grille', 'REMOVE REGISTER OR GRILLE. CAP DUCT.'],
  ['mech.poolEquipment', 'Pool equipment', 'REMOVE POOL EQUIPMENT AND ASSOCIATED PIPING AND ELECTRICAL.'],
  ['elevator', 'Elevator', 'REMOVE ELEVATOR AND ASSOCIATED EQUIPMENT.'],
  ['elec.device', 'Outlet / switch / device', 'REMOVE DEVICE AND COVER PLATE. REMOVE WIRING TO SOURCE OR MAKE SAFE.'],
  ['elec.light', 'Light fixture', 'REMOVE LIGHT FIXTURE. REMOVE WIRING TO SOURCE OR MAKE SAFE.'],
  ['elec.panel', 'Panel / meter / electrical equipment', 'REMOVE ELECTRICAL EQUIPMENT. COORDINATE SHUTOFF WITH UTILITY.'],
  ['bath.accessory', 'Bath accessory / mirror', 'REMOVE BATH ACCESSORY. PATCH WALL.'],
  ['stair', 'Stair', 'REMOVE STAIR, LANDINGS AND HANDRAILS.'],
  ['ramp', 'Ramp', 'REMOVE RAMP AND RAILINGS.'],
  ['railing', 'Railing', 'REMOVE RAILING AND ATTACHMENTS. PATCH SURFACES.'],
  ['fence', 'Fence', 'REMOVE FENCE, POSTS AND FOOTINGS. BACKFILL AND RESTORE GRADE.'],
  ['paving', 'Paving', 'REMOVE PAVING AND BASE MATERIAL. SAWCUT CLEAN EDGE AT REMAINING PAVING.'],
  // STREETS (2026-10-20, docs/bim-plan/35 step 3): an existing street's curb and walk where a
  // new driveway approach replaces them (derived from the junction, not a demolished element)
  ['paving.curbCut', 'Curb cut for a new driveway approach', 'SAWCUT AND REMOVE EXISTING CURB AND SIDEWALK FOR NEW DRIVEWAY APPROACH. SAWCUT AT NEAREST SCORE JOINT.'],
  ['floor', 'Floor area', 'REMOVE FLOOR ASSEMBLY IN AREA SHOWN, INCLUDING FINISHES.'],
  ['ceiling', 'Ceiling area', 'REMOVE CEILING IN AREA SHOWN, INCLUDING FINISHES AND FIXTURES.'],
  ['column', 'Column', 'REMOVE COLUMN. PROVIDE TEMPORARY SHORING AS REQUIRED.'],
  ['beam', 'Beam', 'REMOVE BEAM. PROVIDE TEMPORARY SHORING AS REQUIRED.'],
  ['pool', 'Pool / spa', 'REMOVE POOL OR SPA, COPING AND ASSOCIATED EQUIPMENT.'],
  // DEMOLISH LAYERS (2026-10-14/15, docs/bim-plan/32 §3 E): a wall that stays while layers come
  // off it. Matt: the siding coming off always means a new weather-resistive barrier, "part of
  // the scope somehow ... a construction note to capture it in the drawings" — said here, where
  // the removal is.
  ['wallLayers.exteriorFinish', 'Exterior finish off (sheathing and framing stay)', 'REMOVE EXISTING EXTERIOR FINISH DOWN TO SHEATHING. EXISTING SHEATHING AND FRAMING TO REMAIN. PREPARE FOR NEW WEATHER-RESISTIVE BARRIER AND FINISH.'],
  ['wallLayers.exteriorToStuds', 'Exterior finish and sheathing off (framing stays)', 'REMOVE EXISTING EXTERIOR FINISH AND SHEATHING DOWN TO STUDS. EXISTING FRAMING TO REMAIN. PREPARE FOR NEW SHEATHING, WEATHER-RESISTIVE BARRIER AND FINISH.'],
  ['wallLayers.interiorFinish', 'Interior finish off (framing stays)', 'REMOVE EXISTING INTERIOR FINISH DOWN TO STUDS. EXISTING FRAMING TO REMAIN.'],
  ['wallLayers.insulation', 'Insulation out of an existing wall', 'REMOVE EXISTING INSULATION FROM WALL CAVITY.'],
  // ROOF KEYNOTES (2026-10-30, Matt): the demolition roof plan — a roof (or an area of one) going,
  // and an existing roof that stays while its roofing comes off or its eaves change (Demolish
  // Layers on a roof; Eaves as New Construction). Appended, so no firm number shifts.
  ['roof', 'Roof or roof area', 'REMOVE ROOF IN AREA SHOWN, INCLUDING ROOFING, SHEATHING, FRAMING, EAVES, GUTTERS AND DOWNSPOUTS. SHORE AS REQUIRED.'],
  ['roofLayers.roofing', 'Roofing off (sheathing and framing stay)', 'REMOVE EXISTING ROOFING AND UNDERLAYMENT DOWN TO SHEATHING. EXISTING SHEATHING AND FRAMING TO REMAIN. PREPARE FOR NEW UNDERLAYMENT AND ROOFING.'],
  ['roofLayers.toRafters', 'Roofing and sheathing off (framing stays)', 'REMOVE EXISTING ROOFING, UNDERLAYMENT AND SHEATHING DOWN TO RAFTERS. EXISTING FRAMING TO REMAIN. PREPARE FOR NEW SHEATHING, UNDERLAYMENT AND ROOFING.'],
  ['roofEave.overhang', 'Eave overhang changing', 'REMOVE EXISTING EAVE PAST THE WALL LINE AT EAVES SHOWN, INCLUDING ROOFING, SHEATHING, FASCIA, GUTTERS AND EAVE FRAMING, FOR NEW EAVE.'],
  ['roofEave.fascia', 'Fascia / frieze / barge at an eave', 'REMOVE EXISTING FASCIA, FRIEZE AND BARGE BOARDS AT EAVES SHOWN.'],
  ['roofEave.gutters', 'Gutters at an eave', 'REMOVE EXISTING GUTTERS AND DOWNSPOUTS AT EAVES SHOWN. PROTECT OR CAP DRAIN LINES.'],
  ['roofEave.soffit', 'Soffit at an eave', 'REMOVE EXISTING SOFFIT AND SOFFIT FRAMING AT EAVES SHOWN.'],
  ['roofEave.tails', 'Rafter tails / outlookers at an eave', 'REMOVE EXISTING RAFTER TAILS, OUTLOOKERS AND EAVE FRAMING AT EAVES SHOWN. EXISTING RAFTERS BEHIND THE WALL LINE TO REMAIN.'],
].map(([key, appliesTo, text]) => ({ key, appliesTo, text, active: true }))

/**
 * CONSTRUCTION KEYNOTES (2026-10-15, docs/bim-plan/32 §4 item 3): the new-work side of a wall
 * that stays while layers come off or go on, tagged on that phase's construction plan (square
 * tags, their own list — the demolition list keeps the diamonds). Defaults only for now (no firm
 * table yet); wording to be tuned with Matt.
 */
export const DEFAULT_WORK_KEYNOTES = [
  ['work.exteriorFinish', 'New exterior finish on existing sheathing', 'NEW EXTERIOR FINISH OVER NEW WEATHER-RESISTIVE BARRIER ON EXISTING SHEATHING. EXISTING FRAMING TO REMAIN.'],
  ['work.exteriorFinishSheathing', 'New exterior finish and sheathing', 'NEW EXTERIOR FINISH OVER NEW WEATHER-RESISTIVE BARRIER ON NEW SHEATHING. EXISTING FRAMING TO REMAIN.'],
  ['work.exteriorFinishOnFraming', 'New exterior finish straight on the framing', 'NEW EXTERIOR FINISH OVER NEW WEATHER-RESISTIVE BARRIER ON EXISTING FRAMING.'],
  ['work.interiorFinish', 'New interior finish', 'NEW INTERIOR FINISH ON EXISTING FRAMING.'],
  ['work.replaceInKind', 'Wall layers put back the same', 'REPLACE REMOVED WALL LAYERS IN KIND TO MATCH EXISTING.'],
  ['work.insulation', 'New insulation in an existing wall', 'NEW INSULATION IN EXISTING WALL CAVITY.'],
  // ROOF KEYNOTES (2026-10-30): the new-work roof plan
  ['roofWork.reroof', 'New roofing on existing sheathing', 'NEW ROOFING OVER NEW UNDERLAYMENT ON EXISTING SHEATHING. EXISTING FRAMING TO REMAIN.'],
  ['roofWork.reroofSheathing', 'New roofing and sheathing', 'NEW ROOFING OVER NEW UNDERLAYMENT ON NEW SHEATHING. EXISTING FRAMING TO REMAIN.'],
  ['roofWork.replaceInKind', 'Roof layers put back the same', 'REPLACE REMOVED ROOFING LAYERS IN KIND TO MATCH EXISTING.'],
  ['roofWork.overhang', 'New eave overhang', 'NEW EAVE AT EAVES SHOWN. EXTEND OR CUT BACK EXISTING FRAMING TO SUIT.'],
  ['roofWork.fascia', 'New fascia / frieze / barge', 'NEW FASCIA, FRIEZE OR BARGE BOARDS AT EAVES SHOWN.'],
  ['roofWork.gutters', 'New gutters', 'NEW GUTTERS AND DOWNSPOUTS AT EAVES SHOWN.'],
  ['roofWork.soffit', 'New soffit', 'NEW SOFFIT AT EAVES SHOWN.'],
  ['roofWork.tails', 'New rafter tails / outlookers', 'NEW RAFTER TAILS, OUTLOOKERS AND EAVE FRAMING AT EAVES SHOWN.'],
].map(([key, appliesTo, text]) => ({ key, appliesTo, text, active: true }))

/** the list in use — the firm's (loaded from Supabase by the Modeler) or the defaults */
let firmList = null
export function setDemoKeynoteList(list) { firmList = Array.isArray(list) && list.length ? list : null }
export function demoKeynoteList() { return firmList || DEFAULT_DEMO_KEYNOTES }

/** key → { num, text } for the ACTIVE keynotes (number = position in the list) */
function keynoteIndex(mode = 'demo') {
  const out = new Map()
  let n = 0
  for (const k of mode === 'work' ? DEFAULT_WORK_KEYNOTES : demoKeynoteList()) {
    if (k.active === false) continue
    n++
    out.set(k.key, { num: n, text: k.text })
  }
  return out
}

/** which keynote a demolished element takes, or null (furniture, entourage …) */
export function keynoteKeyFor(el) {
  const p = el.params || {}
  switch (el.category) {
    case 'wall': return p.function === 'interior' ? 'wall.interior' : 'wall.exterior'
    case 'door': {
      const op = p.operation
      if (SLIDING.includes(op)) return 'door.sliding'
      if (FOLDING.includes(op)) return 'door.folding'
      if (op === 'pocket') return 'door.pocket'
      if (GARAGE.includes(op)) return 'door.garage'
      if (op === 'casedOpening') return 'door.cased'
      if (op === 'gateSwing' || op === 'gateSlide') return 'door.gate'
      return 'door'
    }
    case 'window': return 'window'
    case 'casework': return p.style === 'closet' ? 'casework.closet' : 'casework'
    case 'stair': return 'stair'
    case 'ramp': return 'ramp'
    case 'railing': return 'railing'
    case 'fence': return 'fence'
    case 'paving': return 'paving'
    case 'floor': return 'floor'
    case 'ceiling': return 'ceiling'
    case 'column': return 'column'
    case 'beam': return 'beam'
    case 'pool': return 'pool'
    case 'object': {
      const k = p.objectKind
      if (k === 'kitchenSink') return 'plumbing.kitchenSink'
      if (k === 'bathSink') return 'plumbing.lavatory'
      if (k === 'toilet' || k === 'wallHungToilet') return 'plumbing.toilet'
      if (k === 'tub' || k === 'tubFreestanding') return 'plumbing.tub'
      if (typeof k === 'string' && k.startsWith('shower')) return 'plumbing.shower'
      if (k === 'range') return 'appliance.range'
      if (k === 'hoodInsert') return 'appliance.hood'
      if (k === 'fridge' || k === 'columnFridge') return 'appliance.refrigerator'
      if (k === 'dishwasher') return 'appliance.dishwasher'
      if (k === 'washer' || k === 'dryer' || k === 'washerDryerCombo') return 'appliance.laundry'
      if (k === 'fireplaceLinear' || k === 'fireplaceWood' || k === 'woodStove') return 'fireplace'
      if (k === 'waterHeater' || k === 'tanklessWaterHeater') return 'mech.waterHeater'
      if (k === 'furnace' || k === 'airHandler' || k === 'boiler') return 'mech.hvac'
      if (k === 'condenser' || k === 'miniSplitHead' || k === 'miniSplitOutdoor') return 'mech.condenser'
      if (k === 'floorRegister' || k === 'returnGrille') return 'mech.register'
      if (k === 'poolEquipment') return 'mech.poolEquipment'
      if (k === 'elevator') return 'elevator'
      return null   // furniture, entourage, rugs, TVs: owner items, not demolition
    }
    case 'fixture': {
      const k = p.fixtureKind
      if (['outlet', 'switch', 'lowVoltage', 'thermostat'].includes(k)) return 'elec.device'
      if (['sconce', 'recessedCan', 'pendant', 'flushMount'].includes(k)) return 'elec.light'
      if (['panel', 'subpanel', 'meter', 'disconnect', 'inverter', 'evCharger'].includes(k)) return 'elec.panel'
      if (['grabBar', 'towelBar', 'towelRing', 'robeHook', 'toiletPaperHolder', 'mirror', 'medicineCabinet'].includes(k)) return 'bath.accessory'
      return null
    }
    default: return null
  }
}

// ---------- the demolished items on one plan view ----------

/**
 * One entry per demolished item this demolition plan shows: `{ id, key, num, text,
 * anchor, exterior, side? }`. `view` is the plan's ViewRecord (its level, phase and
 * composed category visibility decide membership).
 */
export function demoKeynoteItems(model, view, mode = 'demo') {
  const scope = view?.scope
  if (!scope || scope.kind !== 'plan' || !(scope.levelId || scope.planType === 'roof')) return []
  const phases = listPhases(model)
  const viewPhase = view.phase || (phases.length ? phases[phases.length - 1].id : null)
  if (!viewPhase) return []
  const hidden = new Set(Object.entries(composedOverrides(view) || {}).filter(([, o]) => o?.visible === false).map(([c]) => c))
  const index = keynoteIndex(mode)
  // ROOF KEYNOTES: a roof plan has no level — it tags the roofs, and only the roofs
  if (!scope.levelId) return roofKeynoteItems(model, view, phases, viewPhase, index, mode, hidden)
  const els = resolveAllElements(model)
  const byId = new Map(els.map((e) => [e.id, e]))
  const levelId = scope.levelId
  const viewHidden = new Set(view.hiddenElements || [])

  // every wall on this level (the building's edge + the ray test), exterior ones apart
  const levelWalls = els.filter((e) => e.category === 'wall' && e.levelId === levelId && e.params.path?.nodes?.length >= 2)
  const allSegs = []
  const extSegs = []
  for (const w of levelWalls) {
    const pts = flattenPathPts(w.params.path)
    for (let i = 0; i + 1 < pts.length; i++) {
      const seg = { a: pts[i], b: pts[i + 1], half: (w.assemblyThicknessFt || 0.5) / 2 }
      allSegs.push(seg)
      if (w.params.function !== 'interior') extSegs.push(seg)
    }
  }
  // which side of a wall face is OUTSIDE: the side whose straight run outward meets
  // fewer walls of this level (zero, for a true exterior face) — works on L-shapes
  const hitsAlong = (from, dir) => {
    const far = [from[0] + dir[0] * 400, from[1] + dir[1] * 400]
    let n = 0
    for (const sg of allSegs) if (segCross(from, far, sg.a, sg.b)) n++
    return n
  }
  const outwardAt = (pt, dir, half) => {
    const L = [-dir[1], dir[0]]
    const pA = [pt[0] + L[0] * (half + 0.2), pt[1] + L[1] * (half + 0.2)]
    const pB = [pt[0] - L[0] * (half + 0.2), pt[1] - L[1] * (half + 0.2)]
    return hitsAlong(pA, L) <= hitsAlong(pB, [-L[0], -L[1]]) ? L : [-L[0], -L[1]]
  }
  /** an exterior item's rail: the wall face it sits at, as (outward normal n, face offset c) */
  const faceFor = (pt, dir, half) => {
    const n = outwardAt(pt, dir, half)
    return { n, c: pt[0] * n[0] + pt[1] * n[1] + half }
  }
  const nearestExt = (pt) => {
    let best = null, bd = Infinity
    for (const sg of extSegs) { const d = distPtSeg(pt, sg.a, sg.b); if (d < bd) { bd = d; best = sg } }
    return bd < 3 ? best : null
  }

  const items = []
  const wallPieces = []
  for (const el of mode === 'work' ? [] : els) {
    if (hidden.has(el.category) || viewHidden.has(el.id)) continue
    const host = el.hostId ? byId.get(el.hostId) : null
    const elLevel = host && (el.category === 'door' || el.category === 'window' || !el.params?.path?.nodes?.length) ? host.levelId : el.levelId
    if (elLevel !== levelId) continue
    const fields = hostedPhaseFields(el, (id) => byId.get(id), phases)
    if (phaseStatus(phases, fields, viewPhase) !== 'demo') continue
    // goes only because its host wall goes → the wall's note covers it
    if (host && host.category === 'wall' && !el.phaseDemolished && phaseStatus(phases, host, viewPhase) === 'demo') continue
    const key = keynoteKeyFor(el)
    const k = key && index.get(key)
    if (!k) continue
    if (el.category === 'wall') {
      const pts = flattenPathPts(el.params.path)
      for (let i = 0; i + 1 < pts.length; i++) wallPieces.push({ el, key, k, a: pts[i], b: pts[i + 1], half: (el.assemblyThicknessFt || 0.5) / 2 })
      continue
    }
    let anchor = null, face = null
    if ((el.category === 'door' || el.category === 'window') && host?.params?.path?.nodes?.length >= 2) {
      const pts = flattenPathPts(host.params.path)
      const { pt, dir } = polylinePointAt(pts, el.params.hostOffsetFt || 0)
      anchor = [pt[0], pt[1]]
      if (host.params.function !== 'interior') face = faceFor(anchor, dir, (host.assemblyThicknessFt || 0.5) / 2)
    } else {
      let fp = null
      try { fp = elementFootprint(model, el) } catch { fp = null }
      if (!fp) {
        const n0 = el.params?.path?.nodes?.[0]?.pt
        if (!n0) continue
        anchor = [n0[0], n0[1]]
      } else anchor = [fp.cx, fp.cy]
      const sg = nearestExt(anchor)
      if (sg) {
        const L = Math.hypot(sg.b[0] - sg.a[0], sg.b[1] - sg.a[1]) || 1
        face = faceFor(projectOn(anchor, sg.a, sg.b), [(sg.b[0] - sg.a[0]) / L, (sg.b[1] - sg.a[1]) / L], sg.half)
      }
    }
    items.push({ id: el.id, key, num: k.num, text: k.text, anchor, exterior: !!face, face })
  }

  // ---- STREETS (2026-10-20, docs/bim-plan/35 step 3): an existing street whose curb a driveway
  // built in THIS phase cuts — the removed curb and walk tag at the cut's middle
  if (mode !== 'work') {
    const kc = index.get('paving.curbCut')
    let junctions = null
    try { junctions = kc ? streetJunctions(byId) : null } catch { junctions = null }
    for (const [streetId, cuts] of junctions ? junctions.byStreet : []) {
      const street = byId.get(streetId)
      if (!street || street.levelId !== levelId || hidden.has('paving') || viewHidden.has(streetId)) continue
      const sst = phaseStatus(phases, street, viewPhase)
      if (sst !== 'existing') continue
      for (const c of cuts) {
        const run = byId.get(c.runId)
        if (!run || phaseStatus(phases, run, viewPhase) !== 'new') continue
        let surf = null
        try { surf = pavingSurface(street, byId) } catch { surf = null }
        const sp = surf?.spec?.street
        if (!sp) continue
        const p = surf.ribbon.at(c.sc, sp.side * Math.max(sp.backOfCurbU, (sp.approach?.depthFt ?? sp.backOfCurbU) / 2))
        items.push({ id: `curbcut:${streetId}:${c.runId}`, key: 'paving.curbCut', num: kc.num, text: kc.text, anchor: [p[0], p[1]], exterior: false, face: null })
      }
    }
  }

  // ---- DEMOLISH LAYERS: a wall that stays while layers come off it in this phase — one piece per
  // face note (exterior finish / to studs, interior finish, insulation), run-grouped below ----
  for (const el of els) {
    if (el.category !== 'wall' || el.levelId !== levelId || hidden.has('wall') || viewHidden.has(el.id)) continue
    if (!el.assembly || !el.params.path?.nodes?.length) continue
    if (phaseStatus(phases, el, viewPhase) === 'demo') continue   // the whole wall's note covers it
    const sc = migrateWallScope(normalizeScope(el.params.scope), el.assembly)
    if (!sc || sc.phase !== viewPhase) continue
    const keys = wallLayerKeys(el.assembly)
    const gone = (i) => { const v = scopeVerbOf(sc, keys[i]); return v === 'remove' || v === 'replace' }
    const ext = faceLayerOrder(el.assembly, 'exterior'), int = faceLayerOrder(el.assembly, 'interior')
    if (mode === 'work') {
      // the new work on a wall that stays: a new finish (with new sheathing under it when the
      // sheathing comes back too), layers put back the same, new insulation
      const notes = []
      const after = el.scopeAfter
      const addedExt = after ? after.assembly.layers.slice(0, after.added.exterior) : []
      const back = (i) => scopeVerbOf(sc, keys[i]) === 'replace'
      if (sc.add?.exterior) {
        const newSheath = addedExt.some((l) => l.function === 'substrate') || ext.some((i) => back(i) && el.assembly.layers[i].function === 'substrate')
        const afterCore = after ? after.assembly.coreBoundary?.start ?? 0 : 0
        const sheathAfter = !!after && after.assembly.layers.some((l, i) => i < afterCore && l.function === 'substrate')
        notes.push(newSheath ? 'work.exteriorFinishSheathing' : sheathAfter ? 'work.exteriorFinish' : 'work.exteriorFinishOnFraming')
      }
      if (sc.add?.interior) notes.push('work.interiorFinish')
      if ((!sc.add?.exterior && ext.some(back)) || (!sc.add?.interior && int.some(back))) notes.push('work.replaceInKind')
      if (scopeVerbOf(sc, 'insulation') === 'replace') notes.push('work.insulation')
      const pts = flattenPathPts(el.params.path)
      for (const key of notes) {
        const k = index.get(key)
        if (!k) continue
        for (let i = 0; i + 1 < pts.length; i++) wallPieces.push({ el, key, k, a: pts[i], b: pts[i + 1], half: (el.assemblyThicknessFt || 0.5) / 2 })
      }
      continue
    }
    const notes = []
    if (ext.some(gone)) notes.push(ext.every(gone) && ext.some((i) => el.assembly.layers[i].function === 'substrate') ? 'wallLayers.exteriorToStuds' : 'wallLayers.exteriorFinish')
    if (int.some(gone)) notes.push('wallLayers.interiorFinish')
    const iv = scopeVerbOf(sc, 'insulation')
    if (iv === 'remove' || iv === 'replace') notes.push('wallLayers.insulation')
    const pts = flattenPathPts(el.params.path)
    for (const key of notes) {
      const k = index.get(key)
      if (!k) continue
      for (let i = 0; i + 1 < pts.length; i++) wallPieces.push({ el, key, k, a: pts[i], b: pts[i + 1], half: (el.assemblyThicknessFt || 0.5) / 2 })
    }
  }

  // ---- walls: one tag per RUN — every demolished wall piece with the same keynote
  // that connects to another (end to end, at a corner, or butting into it) is one run,
  // tagged once at the middle of its longest leg ----
  const parent = wallPieces.map((_, i) => i)
  const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])))
  const touches = (p, q) => {
    for (const e of [p.a, p.b]) if (distPtSeg(e, q.a, q.b) < Math.max(p.half, q.half) + 0.3) return true
    for (const e of [q.a, q.b]) if (distPtSeg(e, p.a, p.b) < Math.max(p.half, q.half) + 0.3) return true
    return false
  }
  for (let i = 0; i < wallPieces.length; i++) {
    for (let j = i + 1; j < wallPieces.length; j++) {
      if (wallPieces[i].key !== wallPieces[j].key) continue
      if (touches(wallPieces[i], wallPieces[j])) parent[find(i)] = find(j)
    }
  }
  const groups = new Map()
  wallPieces.forEach((pc, i) => {
    const r = find(i)
    if (!groups.has(r)) groups.set(r, [])
    groups.get(r).push(pc)
  })
  for (const list of groups.values()) {
    // the longest leg: collinear pieces of the group merged into straight legs first
    const legs = []
    for (const pc of list) {
      const dx = pc.b[0] - pc.a[0], dy = pc.b[1] - pc.a[1]
      const L = Math.hypot(dx, dy)
      if (L < 1e-6) continue
      let u = [dx / L, dy / L]
      if (u[0] < -1e-9 || (Math.abs(u[0]) < 1e-9 && u[1] < 0)) u = [-u[0], -u[1]]
      const c = -u[1] * pc.a[0] + u[0] * pc.a[1]
      const ta = pc.a[0] * u[0] + pc.a[1] * u[1], tb = pc.b[0] * u[0] + pc.b[1] * u[1]
      const t0 = Math.min(ta, tb), t1 = Math.max(ta, tb)
      const leg = legs.find((g) => Math.abs(g.c - c) < 0.15 && Math.abs(g.u[0] * u[1] - g.u[1] * u[0]) < 0.01 && t0 <= g.t1 + 0.3 && t1 >= g.t0 - 0.3)
      if (leg) { leg.t0 = Math.min(leg.t0, t0); leg.t1 = Math.max(leg.t1, t1); leg.half = Math.max(leg.half, pc.half) }
      else legs.push({ u, c, t0, t1, half: pc.half })
    }
    if (!legs.length) continue
    const leg = legs.reduce((m, g) => (g.t1 - g.t0 > m.t1 - m.t0 ? g : m))
    const tm = (leg.t0 + leg.t1) / 2
    const anchor = [leg.u[0] * tm - leg.u[1] * leg.c, leg.u[1] * tm + leg.u[0] * leg.c]
    const first = list[0]
    const face = first.key === 'wall.exterior' || first.key.startsWith('wallLayers.exterior') || first.key.startsWith('work.exterior') ? faceFor(anchor, leg.u, leg.half) : null
    items.push({ id: first.el.id, key: first.key, num: first.k.num, text: first.k.text, anchor, exterior: !!face, face })
  }
  return items
}

// ---------- ROOF KEYNOTES (2026-10-30): the demolition / new-work roof plan ----------

const ROOF_PART_NOTE = { structure: 'overhang', fascia: 'fascia', gutters: 'gutters', soffit: 'soffit', eaveFraming: 'tails' }

/**
 * The roof plan's items: a roof (or a phase area of one) demolished in the view's phase; an
 * existing roof's roofing coming off / going on (its scope, Demolish Layers + the trowel); and its
 * eaves changing (Eaves as New Construction, project eave sets, or the Scope panel's eave parts),
 * tagged once per RUN of neighbouring edges with the same note, out on the eave line where rails
 * line them up. "Remove" notes only where the part was; "new" notes only where it goes.
 */
function roofKeynoteItems(model, view, phases, viewPhase, index, mode, hidden) {
  if (hidden.has('roof')) return []
  const viewHidden = new Set(view.hiddenElements || [])
  const sorted = [...phases].sort((a, b) => a.order - b.order)
  const vi = sorted.findIndex((p) => p.id === viewPhase)
  const prevPhase = vi > 0 ? sorted[vi - 1].id : null
  const work = mode === 'work'
  const items = []
  const push = (id, key, anchor, face = null) => {
    const k = index.get(key)
    if (k) items.push({ id, key, num: k.num, text: k.text, anchor, exterior: !!face, face })
  }
  for (const el of resolveAllElements(model)) {
    if (el.category !== 'roof' || viewHidden.has(el.id)) continue
    const status = phaseStatus(phases, el, viewPhase)
    // a roof going whole, on the demolition plan
    if (!work && status === 'demo') {
      const c = roofMarkerPoint(el)
      if (c) push(el.id, 'roof', [c[0], c[1]])
      continue
    }
    if (status !== 'existing') continue
    // areas of an existing roof going (Phase Split)
    if (!work) {
      for (const a of el.params.phaseAreas || []) {
        if (a?.phaseDemolished !== viewPhase || !a.path?.nodes?.length) continue
        const pts = flattenPathPts(a.path)
        push(`${el.id}:area:${a.id}`, 'roof', [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length])
      }
    }
    const sc = normalizeScope(el.params.scope)
    const inPhase = !!sc && sc.phase === viewPhase
    // the roofing, PLANE BY PLANE (2026-11-02): each note once per roof, over the largest plane
    // it applies to (a whole re-roof is still one tag)
    if (inPhase) {
      const ids = (el.params.boundary?.nodes || []).map((n) => n.id).filter(Boolean)
      const noteFor = (id) => {
        const v = (k) => roofPlaneVerb(sc, id, k)
        const gone = (k) => v(k) === 'remove' || v(k) === 'replace'
        if (!work) return gone('roofing') ? (gone('sheathing') ? 'roofLayers.toRafters' : 'roofLayers.roofing') : null
        if (id && sc.add?.roofing?.[id] && v('roofing') === 'remove') return roofSheathingWork(el, id) === 'replace' ? 'roofWork.reroofSheathing' : 'roofWork.reroof'
        if (v('roofing') === 'replace' || v('sheathing') === 'replace') return 'roofWork.replaceInKind'
        return null
      }
      const byNote = new Map()
      for (const id of ids.length ? ids : [null]) {
        const k = noteFor(id)
        if (k) byNote.set(k, [...(byNote.get(k) || []), id])
      }
      for (const [k, planes] of byNote) {
        let best = null, area = -1
        for (const id of planes) {
          const a = roofPlaneArea(el, id)
          if (a > area) { area = a; best = id }
        }
        const c = roofMarkerPoint(el, best) ?? roofMarkerPoint(el)
        if (c) push(`${el.id}:roofing:${k}`, k, [c[0], c[1]])
      }
    }
    // the eaves: what changes on each edge in the view's phase
    const before = prevPhase ? withResolvePhase(prevPhase, () => resolveElement(model, el.id)) : null
    const after = withResolvePhase(viewPhase, () => resolveElement(model, el.id))
    if (!after) continue
    const parts = new Map()
    for (const c of (before && eaveEdgeChanges(before, after)) || []) parts.set(c.nodeId, new Set(c.parts))
    if (inPhase) {
      // the Scope panel's eave parts are whole-roof (in kind)
      for (const p of ['fascia', 'gutters', 'soffit', 'eaveFraming']) {
        const v = scopeVerbOf(sc, p)
        if (v !== 'remove' && v !== 'replace') continue
        for (const n of after.params.boundary?.nodes || []) {
          if (!parts.has(n.id)) parts.set(n.id, new Set())
          parts.get(n.id).add(p)
        }
      }
    }
    if (!parts.size) continue
    const had = before ? eavePartsPresent(before) : new Map()
    const has = eavePartsPresent(after)
    const nodes = after.params.boundary?.nodes || []
    const pfx = work ? 'roofWork.' : 'roofEave.'
    const keyAt = (i) => {
      const id = nodes[i]?.id
      const ps = id && parts.get(id)
      if (!ps) return []
      const there = (work ? has : had).get(id) || new Set()
      if (ps.has('structure')) return there.has('structure') ? [pfx + 'overhang'] : []
      return ['fascia', 'gutters', 'soffit', 'eaveFraming'].filter((p) => ps.has(p) && there.has(p)).map((p) => pfx + ROOF_PART_NOTE[p])
    }
    for (const it of roofEdgeRuns(after, nodes, keyAt)) push(`${el.id}:${it.key}:${it.first}`, it.key, it.anchor, it.face)
  }
  return items
}

/** runs of neighbouring boundary edges sharing a note, each tagged at the middle of its longest
 *  edge, on the eave line (the bearing line pushed out by that edge's overhang), facing out */
function roofEdgeRuns(el, nodes, keyAt) {
  const n = nodes.length
  if (n < 3) return []
  let area = 0
  for (let i = 0; i < n; i++) { const a = nodes[i].pt, b = nodes[(i + 1) % n].pt; area += a[0] * b[1] - b[0] * a[1] }
  const sgn = area >= 0 ? 1 : -1
  const dflt = Number(el.params.overhangFt ?? el.typeParams?.overhangFt ?? 0) || 0
  const keysOf = nodes.map((_, i) => keyAt(i))
  const runItem = (start, len, key) => {
    let best = -1, bl = -1
    for (let j = 0; j < len; j++) {
      const i = (start + j) % n, a = nodes[i].pt, b = nodes[(i + 1) % n].pt
      const L = Math.hypot(b[0] - a[0], b[1] - a[1])
      if (L > bl) { bl = L; best = i }
    }
    if (best < 0 || bl < 1e-6) return null
    const a = nodes[best].pt, b = nodes[(best + 1) % n].pt
    const nrm = [(sgn * (b[1] - a[1])) / bl, (-sgn * (b[0] - a[0])) / bl]
    const o = Number(nodes[best].edge?.overhangFt ?? dflt) || 0
    const anchor = [(a[0] + b[0]) / 2 + nrm[0] * o, (a[1] + b[1]) / 2 + nrm[1] * o]
    return { key, first: nodes[start].id, anchor, face: { n: nrm, c: anchor[0] * nrm[0] + anchor[1] * nrm[1] } }
  }
  const out = []
  for (const key of new Set(keysOf.flat())) {
    const on = keysOf.map((ks) => ks.includes(key))
    if (on.every(Boolean)) { out.push(runItem(0, n, key)); continue }
    for (let i = 0; i < n; i++) {
      if (!on[i] || on[(i + n - 1) % n]) continue      // a run starts after an edge without it
      let len = 0
      while (len < n && on[(i + len) % n]) len++
      out.push(runItem(i, len, key))
    }
  }
  return out.filter(Boolean)
}

function projectOn(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const L2 = dx * dx + dy * dy
  const t = L2 > 1e-12 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2)) : 0
  return [a[0] + dx * t, a[1] + dy * t]
}

// ---------- layout (paper sizes in inches through `inFt`) ----------

const TAG_IN = 0.28          // diamond across corners
const RAIL_IN = 0.75         // first rail off the building
const GAP_IN = 0.1           // between neighbors on a rail
const TEXT_IN = 3 / 32

function distPtSeg(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const L2 = dx * dx + dy * dy
  const t = L2 > 1e-12 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2)) : 0
  return Math.hypot(a[0] + dx * t - p[0], a[1] + dy * t - p[1])
}

function segCross(a, b, c, d) {
  const o = (p, q, r) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0])
  const d1 = o(a, b, c), d2 = o(a, b, d), d3 = o(c, d, a), d4 = o(c, d, b)
  return d1 * d2 < -1e-12 && d3 * d4 < -1e-12
}

/** does segment pq pass through box b (Liang–Barsky)? */
function segHitsBox(p, q, b) {
  let t0 = 0, t1 = 1
  const dx = q[0] - p[0], dy = q[1] - p[1]
  for (const [pp, qq] of [[-dx, p[0] - b.minX], [dx, b.maxX - p[0]], [-dy, p[1] - b.minY], [dy, b.maxY - p[1]]]) {
    if (Math.abs(pp) < 1e-12) { if (qq < 0) return false; continue }
    const r = qq / pp
    if (pp < 0) { if (r > t1) return false; if (r > t0) t0 = r } else { if (r < t0) return false; if (r < t1) t1 = r }
  }
  return t0 <= t1
}
const shrink = (b, d) => ({ minX: b.minX + d, minY: b.minY + d, maxX: b.maxX - d, maxY: b.maxY - d })

const boxOf = (c, h) => ({ minX: c[0] - h, minY: c[1] - h, maxX: c[0] + h, maxY: c[1] + h })
const overlaps = (a, b) => a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY

/**
 * Places every item: exterior items on the rail of the building side they face (tags in
 * one straight row/column per side, spread apart in order, never overlapping), interior
 * items at the nearest free GRID point around them (a shared lattice, so neighbors line
 * up), refusing spots whose leader would cross a placed leader or whose tag would sit
 * on another tag or an obstacle. Returns placed `{ ...item, pt }`.
 */
export function layoutDemoKeynotes(model, view, inFt, obstacles = [], mode = 'demo') {
  const items = demoKeynoteItems(model, view, mode)
  if (!items.length) return { placed: [], bbox: null }
  const s = inFt(TAG_IN)
  const half = s / 2
  // the building: every wall on the level
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const el of resolveAllElements(model)) {
    // a roof plan has no level: its roofs are the building
    const path = !view.scope.levelId ? (el.category === 'roof' ? el.params.boundary : null)
      : el.category === 'wall' && el.levelId === view.scope.levelId ? el.params.path : null
    if (!path?.nodes?.length) continue
    for (const p of flattenPathPts(path)) {
      minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]); minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1])
    }
  }
  for (const it of items) {
    minX = Math.min(minX, it.anchor[0]); maxX = Math.max(maxX, it.anchor[0]); minY = Math.min(minY, it.anchor[1]); maxY = Math.max(maxY, it.anchor[1])
  }
  const off = inFt(RAIL_IN)
  // wall bodies on the level: interior tags never sit on a wall
  const wallBoxes = []
  for (const el of resolveAllElements(model)) {
    if (el.category !== 'wall' || el.levelId !== view.scope.levelId || !el.params.path?.nodes?.length) continue
    const h2 = (el.assemblyThicknessFt || 0.5) / 2
    const pts = flattenPathPts(el.params.path)
    for (let i = 0; i + 1 < pts.length; i++) {
      const p0 = pts[i], p1 = pts[i + 1]
      const L = Math.hypot(p1[0] - p0[0], p1[1] - p0[1])
      // short boxes along the run, so a diagonal wall doesn't block its whole bounding box
      const n = Math.max(1, Math.ceil(L / (s * 2)))
      for (let j = 0; j < n; j++) {
        const q0 = [p0[0] + (p1[0] - p0[0]) * (j / n), p0[1] + (p1[1] - p0[1]) * (j / n)]
        const q1 = [p0[0] + (p1[0] - p0[0]) * ((j + 1) / n), p0[1] + (p1[1] - p0[1]) * ((j + 1) / n)]
        wallBoxes.push({ minX: Math.min(q0[0], q1[0]) - h2, minY: Math.min(q0[1], q1[1]) - h2, maxX: Math.max(q0[0], q1[0]) + h2, maxY: Math.max(q0[1], q1[1]) + h2 })
      }
    }
  }
  const taken = []
  const leaders = []
  const placed = []

  // ---- exterior: rails — one per wall face, just outside it; tags on a face share
  // one straight line (a row or column), spread apart in order, re-centered on their
  // items. A rail whose tags would land on placed tags steps out one more pitch. ----
  const pitch = s + inFt(GAP_IN)
  const rails = new Map()
  const deferred = []
  for (const it of items.filter((i) => i.exterior)) {
    const ang = Math.round((Math.atan2(it.face.n[1], it.face.n[0]) * 180) / Math.PI)
    const key = `${ang}|${Math.round(it.face.c * 2)}`
    if (!rails.has(key)) rails.set(key, { n: it.face.n, c: it.face.c, list: [] })
    rails.get(key).list.push(it)
  }
  for (const rail of rails.values()) {
    const { n } = rail
    const u = [-n[1], n[0]]
    const along = (it) => it.anchor[0] * u[0] + it.anchor[1] * u[1]
    const list = rail.list.sort((x, y) => along(x) - along(y))
    const pos = list.map(along)
    for (let i = 1; i < pos.length; i++) pos[i] = Math.max(pos[i], pos[i - 1] + pitch)
    const shift = (list.reduce((acc, it) => acc + along(it), 0) - pos.reduce((acc, v) => acc + v, 0)) / pos.length
    let cRail = rail.c + off
    for (let tier = 0; tier < 6; tier++) {
      const pts = pos.map((v) => [n[0] * cRail + u[0] * (v + shift), n[1] * cRail + u[1] * (v + shift)])
      if (tier === 5 || !pts.some((pt) => taken.some((t) => overlaps(boxOf(pt, half), t)))) {
        pts.forEach((pt, i) => {
          const it = list[i]
          // a rail tag whose leader would cross a placed leader, run through a placed tag,
          // or whose tag would sit on a placed leader falls back to the grid instead
          const b = boxOf(pt, half)
          if (leaders.some(([p, q]) => segCross(pt, it.anchor, p, q))
            || taken.some((t) => segHitsBox(pt, it.anchor, shrink(t, half * 0.15)))
            || leaders.some(([p, q]) => segHitsBox(p, q, b))) { deferred.push(it); return }
          placed.push({ ...it, pt, rail: true })
          taken.push(b)
          leaders.push([pt, it.anchor])
        })
        break
      }
      cRail += pitch
    }
  }

  // ---- interior: the grid ----
  const g = s * 1.6
  const snap = (v) => Math.round(v / g) * g
  for (const it of [...deferred, ...items.filter((i) => !i.exterior)]) {
    const cx = snap(it.anchor[0]), cy = snap(it.anchor[1])
    const cands = []
    for (let r = 1; r <= 9; r++) {
      for (let i = -r; i <= r; i++) for (let j = -r; j <= r; j++) {
        if (Math.max(Math.abs(i), Math.abs(j)) !== r) continue
        const pt = [cx + i * g, cy + j * g]
        const d = Math.hypot(pt[0] - it.anchor[0], pt[1] - it.anchor[1])
        if (d < g * 0.9) continue
        cands.push({ pt, d })
      }
    }
    cands.sort((a, b) => a.d - b.d)
    let best = null
    for (const c of cands) {
      const b = boxOf(c.pt, half)
      if (taken.some((t) => overlaps(b, t)) || obstacles.some((o) => overlaps(b, o)) || wallBoxes.some((o) => overlaps(b, o))) continue
      if (leaders.some(([p, q]) => segCross(c.pt, it.anchor, p, q))) continue
      // its leader never runs through another tag, and it never sits on another leader
      if (taken.some((t) => segHitsBox(c.pt, it.anchor, shrink(t, half * 0.15)))) continue
      if (leaders.some(([p, q]) => segHitsBox(p, q, b))) continue
      best = c.pt
      break
    }
    if (!best) best = cands[0]?.pt || [it.anchor[0] + g, it.anchor[1] + g]
    placed.push({ ...it, pt: best })
    taken.push(boxOf(best, half))
    leaders.push([best, it.anchor])
  }
  return { placed, bbox: { minX, minY, maxX, maxY } }
}

/**
 * The drawn tags: a leader from the tag to a dot on the item, then the diamond with
 * the keynote number. `legend` (live plan only) adds the keynote list in a column to
 * the right of the building — a sheet carries it in the title strip instead.
 */
export function demoKeynotePrims(model, view, inFt, { obstacles = [], legend = false, mode = 'demo' } = {}) {
  const { placed, bbox } = layoutDemoKeynotes(model, view, inFt, obstacles, mode)
  const work = mode === 'work'
  const tagPfx = work ? 'work:' : 'demo:'
  if (!placed.length) return []
  const s = inFt(TAG_IN), h = s / 2
  const th = inFt(TEXT_IN)
  const out = []
  // TAG PLACEMENT (2026-10-14): a hand-dragged keynote sits at layout + its offset; its leader
  // re-derives back to the item from wherever the diamond lands
  const offs = getTagOffsets(model, tagScopeKey(view?.scope?.levelId, view?.scope?.planType))
  for (const p0 of placed) {
    const off = offs[tagPfx + p0.id]
    const p = off ? { ...p0, pt: [p0.pt[0] + off[0], p0.pt[1] + off[1]] } : p0
    const [cx, cy] = p.pt
    const dx = p.anchor[0] - cx, dy = p.anchor[1] - cy
    const L = Math.hypot(dx, dy)
    if (L > h) {
      // leave the diamond at its edge toward the item (|x|+|y| = h); a square at its box edge
      const k = work ? h / Math.max(Math.abs(dx), Math.abs(dy)) : h / (Math.abs(dx) + Math.abs(dy))
      out.push({ kind: 'line', a: [cx + dx * k, cy + dy * k], b: p.anchor, w: 0.7 })
      out.push({ kind: 'circle', c: p.anchor, r: inFt(1 / 48), filled: true })
    }
    const tk = { tagKey: tagPfx + p.id, tagOffsetApplied: true }
    out.push({ kind: 'poly', closed: true, w: 1, pts: work ? [[cx - h, cy + h], [cx + h, cy + h], [cx + h, cy - h], [cx - h, cy - h]] : [[cx, cy + h], [cx + h, cy], [cx, cy - h], [cx - h, cy]], ...tk })
    const label = String(p.num)
    out.push({ kind: 'text', at: [cx, cy - th * 0.85 * 0.35], text: label, hFt: th, anchor: 'middle', bold: true, ...tk })
  }
  if (legend && bbox) {
    const used = usedKeynotes(placed)
    // clear of everything on the drawing's right side: the building, the rail tags, the
    // structural grid bubbles, and any demolition elevation markers
    let right = bbox.maxX
    for (const p of placed) right = Math.max(right, p.pt[0] + h)
    for (const g of listGrids(model)) right = Math.max(right, g.a[0] + inFt(0.6), g.b[0] + inFt(0.6))
    for (const v of listViews(model)) {
      if (v.scope?.kind === 'elevation' && v.phaseFilter === 'demo' && v.scope.line) {
        right = Math.max(right, v.scope.line.a[0] + inFt(0.6), v.scope.line.b[0] + inFt(0.6))
      }
    }
    const x0 = right + inFt(0.75)
    let y = bbox.maxY
    out.push({ kind: 'text', at: [x0, y], text: work ? 'CONSTRUCTION KEYNOTES' : 'DEMOLITION KEYNOTES', hFt: th, bold: true })
    y -= th * 2.4
    const wrapW = inFt(3.6)
    for (const k of used) {
      const cy0 = y + th * 0.35
      out.push({ kind: 'poly', closed: true, w: 0.9, pts: work ? [[x0, cy0 + h], [x0 + s, cy0 + h], [x0 + s, cy0 - h], [x0, cy0 - h]] : [[x0 + h, cy0 + h], [x0 + s, cy0], [x0 + h, cy0 - h], [x0, cy0]] })
      out.push({ kind: 'text', at: [x0 + h, y + th * 0.35 - th * 0.8 * 0.35], text: String(k.num), hFt: th, anchor: 'middle', bold: true })
      const rowTop = y
      for (const ln of wrapChars(k.text, Math.max(10, Math.floor(wrapW / (th * 0.6))))) {
        out.push({ kind: 'text', at: [x0 + s + th, y], text: ln, hFt: th })
        y -= th * 1.5
      }
      // a row is never shorter than its diamond, so neighbors never touch
      y = Math.min(y, rowTop - s - th * 0.4) - th * 0.6
    }
  }
  return out.map((p) => ({ component: work ? 'anno.workkeynote' : 'anno.demokeynote', ...p }))
}

/** the distinct keynotes among placed/listed items, in number order */
export function usedKeynotes(items) {
  const m = new Map()
  for (const it of items) if (!m.has(it.num)) m.set(it.num, { num: it.num, text: it.text, key: it.key })
  return [...m.values()].sort((a, b) => a.num - b.num)
}

function wrapChars(text, max) {
  const out = []
  let line = ''
  for (const w of String(text).split(/\s+/)) {
    if (line && (line + ' ' + w).length > max) { out.push(line); line = w } else line = line ? line + ' ' + w : w
  }
  if (line) out.push(line)
  return out
}

/** true for a plan view that is a demolition plan (its phase display shows demolition) */
export function isDemoPlanView(view) {
  return view?.scope?.kind === 'plan' && (!!view.scope.levelId || view.scope.planType === 'roof') && view.phaseFilter === 'demo'
}


/** true for a plan view that shows a phase's construction (not its demolition) */
export function isWorkPlanView(view) {
  return view?.scope?.kind === 'plan' && (!!view.scope.levelId || view.scope.planType === 'roof') && view.phaseFilter !== 'demo' && view.phaseFilter !== 'existing'
}
